import type {
  AlgorhythmMetadataRequest,
  LooksMetadataRequest,
  MovesMetadataRequest,
  SongMetadataRequest,
  StarMetadataRequest,
  WorldsMetadataRequest,
} from '../../contracts/openapi/assetContracts';

/**
 * Shared Registration Contracts
 *
 * Single source of truth for registration workflow types
 * that match actual backend API contracts
 */

// Core registration form data that matches backend expectations
export interface RegistrationFormData {
  // Core taxonomy fields
  layer: string;
  category: string;
  subcategory: string;

  // Display names for taxonomy (for UI display purposes)
  categoryName?: string;
  subcategoryName?: string;

  // Asset metadata
  humanFriendlyName?: string;
  creatorDescription: string;
  assetType: 'base' | 'variant';

  // File data
  file?: File;

  // Variant fields
  baseAssetId?: string;
  baseStarId?: string;  // Layer-specific variant field for Stars
  baseSongId?: string;  // Layer-specific variant field for Songs
  baseLookId?: string;  // Layer-specific variant field for Looks
  baseMoveId?: string;  // Layer-specific variant field for Moves
  baseWorldId?: string; // Layer-specific variant field for Worlds
  baseBrandId?: string; // Layer-specific variant field for Brands
  variantType?: string;
  variantName?: string;
  variantDescription?: string;

  // Layer-specific metadata
  starMetadata?: Partial<StarMetadataRequest>;
  songMetadata?: Partial<SongMetadataRequest>;
  looksMetadata?: Partial<LooksMetadataRequest>;
  movesMetadata?: Partial<MovesMetadataRequest>;
  worldsMetadata?: Partial<WorldsMetadataRequest>;
  layerMetadata?: Record<string, any>;

  // Algorhythm Integration Fields (Phase 1) - API has 7 fields only (no energyLevel)
  algorhythmMetadata?: Partial<AlgorhythmMetadataRequest>;

  // Additional flexible fields for layer-specific data
  [key: string]: any;
}

// Backend AI metadata response structure
export interface AIMetadataResponse {
  success: boolean;
  data: {
    description: string;
    tags: string[];
    filteredTags?: string[];
    layerMetadata: Record<string, any>;
    starMetadata?: Partial<StarMetadataRequest>;
    songMetadata?: Partial<SongMetadataRequest>;
  };
  message?: string;
}

// Backend asset creation response (StoredAssetDTO: id, humanFriendlyName, machineFriendlyAddress)
export interface AssetCreationResponse {
  success: boolean;
  data: {
    id: string;
    humanFriendlyName: string;
    machineFriendlyAddress?: string;
    assetType?: string;
    layer?: string;
    category?: string;
    subcategory?: string;
    baseStarId?: string;
    starMetadata?: any;
    layerMetadata?: any;
    aiMetadata?: any;
    fileUrl?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  message?: string;
}

// Workflow state for step management
export interface WorkflowState {
  currentStep: number;
  totalSteps: number;
  formData: RegistrationFormData;
  aiMetadata?: AIMetadataResponse;
  createdAsset?: AssetCreationResponse;
  isLoading: boolean;
  error?: string;
  isComplete: boolean;
}

// Step component props interface
export interface StepProps {
  onNext: (data?: any) => void;
  onBack: () => void;
  onComplete?: (data: any) => void;
  data?: any;
  isLoading?: boolean;
  error?: string;
}

// Taxonomy data structure from backend
export interface TaxonomyData {
  code: string;
  name: string;
  displayName: string;
  description?: string;
}

export interface TaxonomyResponse {
  success: boolean;
  data: {
    categories?: TaxonomyData[];
    subcategories?: TaxonomyData[];
  };
}

// Error response structure from backend
export interface APIErrorResponse {
  success: false;
  message: string;
  errors?: {
    field: string;
    message: string;
  }[];
  code?: string;
}

// File upload response for temporary uploads
export interface TempFileUploadResponse {
  success: boolean;
  data: {
    tempUrl: string;
    filename: string;
    expiresAt: string;
  };
}

// Type guards for runtime type checking
export const isAIMetadataResponse = (obj: any): obj is AIMetadataResponse => {
  return obj && typeof obj === 'object' && typeof obj.success === 'boolean';
};

export const isAssetCreationResponse = (obj: any): obj is AssetCreationResponse => {
  return obj && typeof obj === 'object' && typeof obj.success === 'boolean' && obj.data && obj.data.id != null;
};

export const isAPIError = (obj: any): obj is APIErrorResponse => {
  return obj && typeof obj === 'object' && obj.success === false;
};
